import type { Translate } from '@/shared/api/errors'
import { formatBytes } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import type { HistoryActor, HistoryRow } from './historyApi'

export const HISTORY_KINDS = [
  'OPENING_BALANCE',
  'TOPUP',
  'PACKAGE_SALE',
  'SUBSCRIPTION_CHARGE',
  'DEVICE_RENTAL',
  'INVOICE_SALE',
  'DISCOUNT',
  'REFUND',
  'ADJUSTMENT',
  'REVERSAL',
  'USAGE_CHARGE',
  'AGENT_DEPOSIT_FUNDING',
  'COMMISSION',
  'CASH_HANDOVER',
] as const

type HistoryKind = (typeof HISTORY_KINDS)[number]

function isHistoryKind(kind: string): kind is HistoryKind {
  return (HISTORY_KINDS as readonly string[]).includes(kind)
}

export function kindLabel(kind: string, t: Translate): string {
  return t(`history.kind.${isHistoryKind(kind) ? kind : 'OTHER'}`)
}

export function actorLabel(actor: HistoryActor | null, t: Translate): string {
  if (actor === null) return t('common.dash')
  switch (actor.type) {
    case 'STAFF':
    case 'AGENT':
      return actor.name ?? t(`history.actor.${actor.type}`)
    case 'PSP':
      return actor.name ? `${t('history.actor.PSP')} · ${actor.name}` : t('history.actor.PSP')
    case 'SYSTEM':
      return t('history.actor.SYSTEM')
    default:
      return actor.name ?? t('common.dash')
  }
}

export function signedMoney(value: Money): string {
  const text = formatMoneyValue(value)
  return value.amount > 0 ? `+${text}` : text
}

export function packageLine(row: HistoryRow): string | null {
  const traffic = row.trafficBytes > 0 ? `+${formatBytes(row.trafficBytes)}` : null
  const parts = [row.packageName, traffic].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(' · ') : null
}
